import { ReactNode } from "react";
import styles from "./MarkdownFormatter.module.scss";

const INLINE_REGEX =
  /\*\*\*(.+?)\*\*\*|\*\*(.+?)\*\*|\*(.+?)\*|==(.+?)==|`(.+?)`/g;

const UNORDERED_LIST_REGEX = /^- (.+)$/;
const ORDERED_LIST_REGEX = /^\d+\. (.+)$/;

function parseInline(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  let lastIndex = 0;

  INLINE_REGEX.lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = INLINE_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    const key = match.index;
    const savedIndex = INLINE_REGEX.lastIndex;

    if (match[1]) {
      // ***bold italic***
      parts.push(
        <strong key={key}>
          <em>{parseInline(match[1])}</em>
        </strong>,
      );
    } else if (match[2]) {
      // **bold**
      parts.push(<strong key={key}>{parseInline(match[2])}</strong>);
    } else if (match[3]) {
      // *italic*
      parts.push(<em key={key}>{parseInline(match[3])}</em>);
    } else if (match[4]) {
      // ==highlight==
      parts.push(
        <mark key={key} className={styles.highlight}>
          {parseInline(match[4])}
        </mark>,
      );
    } else if (match[5]) {
      // `code` — no recursion inside code
      parts.push(
        <code key={key} className={styles.code}>
          {match[5]}
        </code>,
      );
    }

    INLINE_REGEX.lastIndex = savedIndex;
    lastIndex = savedIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts;
}

type Block =
  | { type: "text"; content: string }
  | { type: "ul"; items: string[] }
  | { type: "ol"; items: string[] };

function parseBlocks(text: string): Block[] {
  const lines = text.split("\n");
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const ulMatch = line.match(UNORDERED_LIST_REGEX);
    const olMatch = line.match(ORDERED_LIST_REGEX);

    if (ulMatch) {
      const items: string[] = [ulMatch[1]];
      i++;
      while (i < lines.length) {
        const nextUl = lines[i].match(UNORDERED_LIST_REGEX);
        if (!nextUl) break;
        items.push(nextUl[1]);
        i++;
      }
      blocks.push({ type: "ul", items });
    } else if (olMatch) {
      const items: string[] = [olMatch[1]];
      i++;
      while (i < lines.length) {
        const nextOl = lines[i].match(ORDERED_LIST_REGEX);
        if (!nextOl) break;
        items.push(nextOl[1]);
        i++;
      }
      blocks.push({ type: "ol", items });
    } else {
      if (line !== "") {
        blocks.push({ type: "text", content: line });
      }
      i++;
    }
  }

  return blocks;
}

function renderBlocks(blocks: Block[]): ReactNode[] {
  return blocks.map((block, index) => {
    switch (block.type) {
      case "text":
        return <span key={index}>{parseInline(block.content)}</span>;
      case "ul":
        return (
          <ul key={index} className={styles.list}>
            {block.items.map((item, i) => (
              <li key={i}>{parseInline(item)}</li>
            ))}
          </ul>
        );
      case "ol":
        return (
          <ol key={index} className={styles.list}>
            {block.items.map((item, i) => (
              <li key={i}>{parseInline(item)}</li>
            ))}
          </ol>
        );
    }
  });
}

/**
 * Formats a string value as markdown content for DataTable cells.
 *
 * Supports inline constructs (bold, italic, bold+italic, inline code, highlight)
 * and block-level flat lists (unordered with `- `, ordered with `N. `).
 *
 * @param value - The markdown string to format
 * @returns Formatted React nodes, or null for empty/null/undefined values
 */
export const markdownFormatter = (value?: string | null): ReactNode => {
  if (value == null || value === "") {
    return null;
  }

  const blocks = parseBlocks(value);

  if (blocks.length === 1 && blocks[0].type === "text") {
    return <>{parseInline(blocks[0].content)}</>;
  }

  return <div className={styles.container}>{renderBlocks(blocks)}</div>;
};
